import React from 'react';
import { Text, View, ScrollView, ImageBackground, ViewStyle } from 'react-native';
import { globalStyles } from 'Theme';
import { Device } from 'DeviceUtils';
import type { StyleMap } from 'Types';
import Hr from '../Hr';
import ModalHeader from '../ModalHeader';
import Button from '../Button';
import Icon from '../Icon';

import defaultStyles from './ConfirmationModal.jss';

interface ConfirmationModalProps {
    icon?: string;
    header?: string;
    message?: string | Array<string>;
    confirmLabel?: string;
    cancelLabel?: string;
    onConfirm?: () => void;
    onCancel?: () => void;
    onClose?: () => void;
    mode?: string | null;
    logo?: string;
    style?: StyleMap;
    headerStyle?: ViewStyle,
    innerContainerStyle?: ViewStyle
}

// eslint-disable-next-line import/prefer-default-export
export const ConfirmationModal = ({
    header,
    message,
    icon,
    confirmLabel,
    cancelLabel,
    onCancel,
    onConfirm,
    onClose,
    mode,
    logo,
    style,
    headerStyle,
    innerContainerStyle
}: ConfirmationModalProps): JSX.Element => {
    const isLandscape = mode === 'landscape';

    const numberOfLines = Device.isWeb ? 0 : 50;

    const onCloseCallback = onClose && typeof onClose === 'function' ? onClose : onCancel;

    return (
        <View
            style={[
                defaultStyles.container,
                Boolean(onCloseCallback) && { backgroundColor: 'rgba(0,0,0,0.9)' },
                style
            ]}
        >
            {onCloseCallback && (
                <ModalHeader
                    style={
                        [isLandscape
                            ? {
                                iconContainer: defaultStyles.iconContainerLandscape
                            }
                            : {}, headerStyle]
                    }
                    onPress={onCloseCallback}
                />
            )}
            <ScrollView contentContainerStyle={[defaultStyles.innerContainer, innerContainerStyle]}>
                {logo && <Icon style={defaultStyles.logo} name={logo} />}
                <View
                    style={[
                        defaultStyles.titleContainer,
                        isLandscape && defaultStyles.titleContainerLandscape
                    ]}
                >
                    {icon && (
                        <Icon
                            style={[
                                defaultStyles.infoIcon,
                                isLandscape && defaultStyles.infoIconLandscape
                            ]}
                            name={icon}
                        />
                    )}
                    {header && <Text style={defaultStyles.title}>{header}</Text>}
                </View>
                <Hr style={defaultStyles.hr} />
                {Array.isArray(message) ? (
                    message.map((msg, index) => (
                        <Text
                            style={globalStyles.secondaryDescription}
                            numberOfLines={numberOfLines}
                            // eslint-disable-next-line react/no-array-index-key
                            key={`msg-line-${index}`}
                        >
                            {msg}
                        </Text>
                    ))
                ) : (
                    <Text style={globalStyles.secondaryDescription} numberOfLines={numberOfLines}>
                        {message}
                    </Text>
                )}
                <View
                    style={[
                        defaultStyles.buttonsContainer,
                        defaultStyles.buttonContainerOneButton,
                        isLandscape && defaultStyles.landscapeButtonsContainer
                    ]}
                >
                    {confirmLabel && (
                        <Button
                            style={{ button: defaultStyles.button }}
                            title={confirmLabel}
                            onPress={onConfirm}
                            testID="confirm_button"
                        />
                    )}
                    {cancelLabel && (
                        <Button
                            style={{ button: defaultStyles.button }}
                            isSecondary
                            title={cancelLabel}
                            onPress={onCancel}
                            testID="cancel_button"
                        />
                    )}
                </View>
            </ScrollView>
        </View>
    );
};
